<template>
  <div class="thank-page container">
    <div class="row d-flex align-items-center">
      <!-- Spinner Loading State -->
      <div class="col-12 d-flex justify-content-center align-items-center" v-if="isLoading">
        <div class="spinner-container">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">{{ $t("Loading...") || "Loading..." }}</p>
        </div>
      </div>
      
      <!-- Main Content -->
      <div class="col-12 d-flex align-items-center flex-column justify-content-center" v-else>
        <h2 class="success-text text-capitalized" v-if="!isPaymentFailed">{{ $t("Thank.done") }}
          <img class="done-img" src="../assets/images/Done.png" alt="done" loading="lazy"/>
        </h2>
        <h2 class="fail-text text-capitalized" v-else>{{ $t("Thank.PaymentFailed") }}</h2>

        <div class="success_message__content">
          <b>{{ $t("Thank.orderNum") }}</b>
          <span>
            <span ref="orderNumber" :style="{ userSelect: userSelect }">{{
              orderNum
            }}</span>
            <img
              v-if="thankData.enable_copy_order_number"
              width="35"
              height="35"
              class="px-2"
              src="@/assets/images/copy.svg"
              :alt="$t('Thank.orderNum')"
              @click="copyOrderNumber" />
          </span>
        </div>
        <div class="success_message__description container">
          <!-- <p>{{ $t("Thank.orderDetails") }}</p> -->
          <div v-html="thankData.thank_you_message"></div>
        </div>
        <div class="success_message__actions mb-3">
          <button
            class="btn btn-dark print"
            aria-label="Print"
            v-if="thankData.enable_invoice_printing"
            @click="printInvoice">
            {{ $t("Thank.printInvoice") }}
            <img
              width="24"
              height="24"
              src="@/assets/images/printer.svg"
              :alt="$t('Thank.printInvoice')" />
          </button>
          <button
            aria-label="Whatsapp"
            class="btn btn-success whatsapp"
            v-if="thankData.redirect_to_whatsapp"
            @click="redirectToWhatsApp">
            {{ $t("Thank.contact") }}
            <img
              width="24"
              height="24"
              src="@/assets/images/whatsapp.svg"
              :alt="$t('Thank.contact')" />
          </button>
          <button
            v-if="thankData.return_to_sales_page_button"
            aria-label="GoBack"
            @click="goToSalesPage"
            class="btn btn-outline-primary back-product">
            {{ $t("Thank.returnToProduct") }}
          </button>
        </div>


        <div class="corss-sell container">
          <CrossSaleContainer :orderID="orderID" :productID="productID" :crossSaleAdded="crossSaleAdded" />
        </div>


      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useClipboard } from "@vueuse/core";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import LayoutService from "@/services/layout";
import handleError from "@/helper/handleError";
import { useRoute } from "vue-router";
import Done from '@/components/icons/done.vue';
import CrossSaleContainer from "./crossSaleContainer.vue";


export default defineComponent({
  name: "ThankView",
  components: {
    Done,
    CrossSaleContainer,
  },
  setup() {
    const { copy } = useClipboard();
    const hasThankYou = ref(true);
    const thankData = ref({
      thank_you_message: "",
      whatsapp_number: "",
      whatsapp_number_code: "",
      enable_invoice_printing: false,
      return_to_sales_page_button: false,
      redirect_to_whatsapp: false,
      product_slug: "",
      enable_copy_order_number: false,
    });
    const isLoading = ref(true);
    const whatsAppNumber = ref("");
    const route = useRoute();
    
    const isPaymentFailed = computed(() => {
      return route.query.status === 'fail';
    });

    // Get from URL first, then fall back to localStorage
    const productID = (route.query.productID as string) || localStorage.getItem('productID');
    const orderNum = (route.query.orderNum as string) || localStorage.getItem('orderNum');
    const orderID = (route.query.orderID as string) || localStorage.getItem('orderID');


    const crossSaleAdded = route.query.crossSaleAdded as string;
    // ======================== Methods ==================
    const copyOrderNumber = () => {
      if (typeof orderNum === "string" && orderNum) {
        copy(orderNum).then(() => {
          toast.success("تم نسخ الكود", {
            autoClose: 3000,
          });
        });
      } else {
        // Handle case where orderNum is null or not a string
        toast.error("لم يتم العثور على رقم الطلب", {
          autoClose: 3000,
        });
      }
    };
    const getThank = () => {

      isLoading.value = true;
      LayoutService.getThank(productID)
        .then((res: any) => {
          isLoading.value = false;
          thankData.value = res.data.data;
          whatsAppNumber.value =
            res.data.data.whatsapp_number_code + res.data.data.whatsapp_number;
        })
        .catch((err: any) => {
          isLoading.value = false;
          handleError(err);
        });
    };
    const redirectToWhatsApp = () => {
      const whatsappNumber = whatsAppNumber.value;
      const whatsappUrl = `https://wa.me/${whatsappNumber}`;
      window.open(whatsappUrl, '_blank');
    };
    // print invoice
    const printInvoice = () => {
      LayoutService.printInvoice(36)
        .then((response: any) => {
          if (response.data.size === 0) {
            return;
          }
          // Continue with file download logic
          const contentType = response.headers["content-type"];
          const blob = new Blob([response.data], {
            type: contentType,
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Invoice.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((err: any) => {
          toast.error(`"Error while downloading invoice:" ${err}`, {
            autoClose: 3000,
          });
        });
    };

    const landingUrl = window.location.origin;

    // return to product page
    const goToSalesPage = () => {
      // Ensure productID is defined and valid
      if (typeof productID === "undefined" || productID === null) {
        return;
      }
      // Construct the URL
      const url = landingUrl+`/products/${thankData.value.product_slug}`;
      // Redirect to the constructed URL
      window.location.href = url;
    };
    // check visit status
    const checkVisitStatus = () => {
      // Check if visit-productID key is false in localStorage
      if (
        localStorage.getItem(`visit-${productID}`) === "false" &&
        localStorage.getItem(`orderNum-${productID}`) === orderNum
      ) {
        // Redirect to the product page
        if (localStorage.getItem(`slug-${productID}`)) {
          window.location.href = landingUrl+`/products/${localStorage.getItem(
            `slug-${productID}`
          )}`;
        }
      }
    };
    // ==================== onMounted =================
    onMounted(() => {
      copyOrderNumber;
      getThank();
    });
    // ========================== computed ==================
    const userSelect = computed(() => {
      return thankData.value.enable_copy_order_number ? "auto" : "none";
    });

    return {
      copyOrderNumber,
      getThank,
      thankData,
      isLoading,
      redirectToWhatsApp,
      printInvoice,
      orderNum,
      productID,
      goToSalesPage,
      checkVisitStatus,
      userSelect,
      hasThankYou,
      orderID,
      crossSaleAdded,
      isPaymentFailed
    };
  },
});
</script>

<style lang="scss" scoped>
.thank-page {
  .row {
    min-height: calc(100vh - 140px);
  }
  .done-img {
    width: 40px;
    height: 40px;
    margin-top: 6px;
  }
}

// Spinner Loading Styles
.spinner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  
  .spinner-border {
    width: 3rem;
    height: 3rem;
    border-width: 0.3em;
  }
  
  p {
    font-size: 16px;
    margin: 0;
    color: #6c757d;
  }
}

// Alternative custom spinner (uncomment if you prefer this over Bootstrap spinner)
/*
.custom-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
*/

.success_message__description {
  font-size: 20px;
  @media (min-width: 320px) and (max-width: 374.98px) {
    font-size: 10px !important;
  }
  @media (min-width: 375px) and (max-width: 424.98px) {
    font-size: 12px !important;
  }
  @media (min-width: 425px) and (max-width: 575.98px) {
    font-size: 14px !important;
  }
  @media (max-width: 768px) {
    font-size: 15px !important;
  }
}
.fail-text {
  color: #da3333;
  font-size: 32px;
  font-weight: 600;
  @media (max-width: 768px) {
    font-size: 24px !important;
  }
}
.success-text {
  color: #2aa952;
  font-size: 32px;
  font-weight: 600;
  @media (max-width: 768px) {
    font-size: 24px !important;
  }
}
.success_message__content {
  margin-top: 0rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 7px;
  font-size: 24px;
  font-weight: 400;
  line-height: 37.5px;
  text-align: center;
  @media (min-width: 320px) and (max-width: 374.98px) {
    font-size: 10px !important;
    margin-top: auto !important;
  }
  @media (min-width: 375px) and (max-width: 424.98px) {
    font-size: 13px !important;
  }
  @media (min-width: 425px) and (max-width: 575.98px) {
    font-size: 14px !important;
  }
  @media (min-width: 768px) {
    font-size: 16px !important;
  }
  @media (min-width: 768px) and (max-width: 991.98px) {
    font-size: 24px!important;
  }
  @media (min-width: 992px) and (max-width: 1399.98px) {
    font-size: 2rem!important;
  }
  p {
    color: rgba(61, 61, 61, 1);
    margin: 0;
  }
  span {
    cursor: pointer;
    color: var(--primary-color);
  }
}

.success_message__actions {
  .btn {
    color: rgba(255, 255, 255, 1);
    border-radius: 12px;
    padding: 5px 31px;
    font-size: 20px;
    font-weight: 400;
    line-height: 37.5px;
    text-align: center;
    margin: 10px;
    border: none;
    min-width: 200px;
    @media (max-width: 768px) {
      min-width: 120px;
      font-size: 14px;
      padding: 0 20px;
    }
  }
  .print {
    background: rgba(65, 65, 65, 1);
  }
  .whatsapp {
    background: rgba(42, 169, 82, 1);
  }
  .back-product {
    color: var(--primary-color);
    border: 1px solid var(--primary-color);
    &:hover {
      background: var(--primary-color);
      color: #fff;
    }
  }
}
</style>